import React, { useState, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { LangContext } from "../../contexts/LangContext";
import { useTemplates } from "../../templates/useTemplates";
import templateTree from "../../templates/templateTree";
import templateData from "../../templates/templateData";
import en from "../../i18n/en";
import pl from "../../i18n/pl";

export function TaskSelectionPage({ onCreateFromScratch, onUseTemplate, onCancel }) {
  const { lang, setLang } = useContext(LangContext);
  const t = lang === "pl" ? pl : en;
  const { tree, getTemplate } = useTemplates();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mode, setMode] = useState("template");
  const [path, setPath] = useState([]);
  const [confirmedLeaf, setConfirmedLeaf] = useState(null);

  const findNode = (nodes, id) => {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.children) {
        const found = findNode(n.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  useEffect(() => {
    const pathParam = searchParams.get("path");
    if (pathParam) {
      try {
        const parsedPath = JSON.parse(decodeURIComponent(pathParam));
        if (Array.isArray(parsedPath) && parsedPath.length > 0) {

          const detectedLang = parsedPath[0]?.startsWith("pl-") ? "pl" : "en";
          if (detectedLang !== lang) setLang(detectedLang);

          const sourceTree = templateTree[detectedLang];
          let valid = true;
          let currentNodes = sourceTree;
          for (const id of parsedPath) {
            const found = currentNodes.find(n => n.id === id);
            if (!found) {
              valid = false;
              break;
            }
            currentNodes = found.children || [];
          }

          if (valid) {
            setPath(parsedPath);
            setMode("template");

            const lastNode = findNode(sourceTree, parsedPath[parsedPath.length - 1]);
            if (lastNode && lastNode.templateId && !lastNode.children?.length) {
              const tpl = getTemplate(lastNode.templateId);
              setConfirmedLeaf({ node: lastNode, tpl });
            }
          }
        }
      } catch (e) {}
    }
  }, []);

  const getCurrentNodes = () => {
    if (path.length === 0) return tree;
    const node = findNode(tree, path[path.length - 1]);
    return node?.children || [];
  };

  const getPathNodes = () => {
    const result = [];
    let nodes = tree;
    for (const id of path) {
      const found = nodes.find((n) => n.id === id);
      if (!found) break;
      result.push(found);
      nodes = found.children || [];
    }
    return result;
  };

  const handleSelectNode = (node) => {
    const hasChildren = node.children?.length > 0;

    if (hasChildren) {
      const newPath = [...path, node.id];
      setPath(newPath);
      setConfirmedLeaf(null);
      setSearchParams({ path: encodeURIComponent(JSON.stringify(newPath)) });
    } else if (node.templateId) {
      const newPath = [...path, node.id];
      setPath(newPath);
      const tpl = getTemplate(node.templateId);
      setConfirmedLeaf({ node, tpl });
      setSearchParams({ path: encodeURIComponent(JSON.stringify(newPath)) });
    }
  };

  const handleUseTemplate = (templateId) => {
    const tpl = templateData[templateId];
    if (tpl) {
      onUseTemplate?.(templateId, tpl.prefill);
    }
  };

  const handleBack = () => {
    if (path.length > 0) {
      const newPath = path.slice(0, -1);
      setPath(newPath);
      setConfirmedLeaf(null);
      if (newPath.length > 0) {
        setSearchParams({ path: encodeURIComponent(JSON.stringify(newPath)) });
      } else {
        setSearchParams({});
      }
    }
  };

  const goToDepth = (depth) => {
    const newPath = path.slice(0, depth + 1);
    setPath(newPath);
    setConfirmedLeaf(null);
    setSearchParams({ path: encodeURIComponent(JSON.stringify(newPath)) });
  };

  const goRoot = () => {
    setPath([]);
    setConfirmedLeaf(null);
    setSearchParams({});
  };

  const nodes = getCurrentNodes();
  const pathNodes = getPathNodes();
  const LEVEL_LABELS = [
    t.taskSelectionPage.levelGoal,
    t.taskSelectionPage.levelForWhom,
    t.taskSelectionPage.levelNiche,
    t.taskSelectionPage.levelSetup
  ];
  const levelLabel = LEVEL_LABELS[Math.min(path.length, LEVEL_LABELS.length - 1)];

  return (
    <div className="add-rule-wrap">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: "clamp(18px,4vw,24px)", margin: 0 }}>
          {t.taskSelectionPage.newTaskTitle}
        </h2>
        <p style={{ margin: "5px 0 0", color: "var(--muted)", fontSize: 13 }}>
          {t.taskSelectionPage.taskSelectionDesc}
        </p>
      </div>

      {/* Mode picker */}
      <div className="section-card" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { id: "scratch", icon: "✏️", title: t.taskSelectionPage.fromScratch, desc: t.taskSelectionPage.fromScratchDesc },
            { id: "template", icon: "📋", title: t.taskSelectionPage.fromTemplate, desc: t.taskSelectionPage.fromTemplateDesc },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => {
                setMode(opt.id);
                setPath([]);
                setConfirmedLeaf(null);
                setSearchParams({});
              }}
              style={{
                padding: 16, borderRadius: 12, fontFamily: "inherit",
                border: `${mode === opt.id ? "2px" : "1.5px"} solid ${mode === opt.id ? "var(--blue)" : "var(--border)"}`,
                background: mode === opt.id ? "var(--blue-light)" : "#fff",
                cursor: "pointer", textAlign: "left", transition: ".15s",
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 8 }}>{opt.icon}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>{opt.title}</div>
              <div style={{ fontSize: 12, marginTop: 4, color: "var(--muted)", lineHeight: 1.5 }}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Template drill-down */}
      {mode === "template" && (
        <div className="section-card">
          {/* Breadcrumb */}
          {path.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
              <button
                onClick={goRoot}
                style={{ fontSize: 12, color: "var(--muted)", background: "none", border: "none", cursor: "pointer", padding: "3px 6px", borderRadius: 6 }}
              >
                {t.taskSelectionPage.templatesLabel}
              </button>
              {pathNodes.map((n, i) => (
                <React.Fragment key={n.id}>
                  <span style={{ fontSize: 11, color: "var(--muted)", opacity: .4 }}>›</span>
                  {i < pathNodes.length - 1 ? (
                    <button
                      onClick={() => goToDepth(i)}
                      style={{ fontSize: 12, color: "var(--muted)", background: "none", border: "none", cursor: "pointer", padding: "3px 6px", borderRadius: 6 }}
                    >
                      {n.label}
                    </button>
                  ) : (
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--dark)", padding: "3px 6px" }}></span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Level label */}
          {!confirmedLeaf && (
            <p style={{ fontSize: 11, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 12 }}>
              {levelLabel}
            </p>
          )}

          {/* Grid of nodes */}
          {!confirmedLeaf && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(155px, 1fr))", gap: 10 }}>
                {nodes.map((node) => {
                  const hasChildren = node.children?.length > 0;
                  return (
                    <button
                      key={node.id}
                      onClick={() => handleSelectNode(node)}
                      style={{
                        background: "#fff", border: "1.5px solid var(--border)", borderRadius: 12,
                        padding: "14px 14px 12px", cursor: "pointer", textAlign: "left",
                        fontFamily: "inherit", transition: ".15s", display: "flex", flexDirection: "column", gap: 4,
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--blue)"; e.currentTarget.style.background = "var(--blue-light)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.background = "#fff"; }}
                    >
                      {node.icon && <span style={{ fontSize: 20, marginBottom: 4 }}>{node.icon}</span>}
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--dark)" }}>{node.label}</span>
                      {node.hint && <span style={{ fontSize: 11, color: "var(--muted)", lineHeight: 1.4 }}>{node.hint}</span>}
                      {node.templateId && hasChildren && (
                        <span style={{ fontSize: 10, background: "#E1F5EE", color: "#085041", padding: "2px 7px", borderRadius: 99, marginTop: 4, display: "inline-block", fontWeight: 600 }}>
                          Has general template
                        </span>
                      )}
                      {hasChildren && (
                        <span style={{ fontSize: 11, color: "var(--muted)", marginTop: "auto", paddingTop: 6 }}>→</span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* General template hint */}
              {path.length > 0 && (() => {
                const currentNode = findNode(tree, path[path.length - 1]);
                return currentNode?.templateId ? (
                  <div style={{ marginTop: 16, background: "#f0f7ff", border: "1.5px solid #bfdbfe", borderRadius: 12, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <span style={{ fontSize: 18 }}>💡</span>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1e40af", marginBottom: 4 }}>
                        {t.taskSelectionPage.orUseGeneralTemplate} "{currentNode.label}"
                      </div>
                      <div style={{ fontSize: 12, color: "#3b5cc8", lineHeight: 1.5, marginBottom: 8 }}>
                        {t.taskSelectionPage.coversBasics}
                      </div>
                      <button
                        onClick={() => handleUseTemplate(currentNode.templateId)}
                        style={{ padding: "6px 14px", borderRadius: 7, border: "none", background: "var(--blue)", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        {t.taskSelectionPage.orUseGeneralTemplate} →
                      </button>
                    </div>
                  </div>
                ) : null;
              })()}
            </>
          )}

          {/* Leaf confirmation */}
          {confirmedLeaf && (
            <div style={{ background: "#f5f8ff", border: "1.5px solid var(--blue)", borderRadius: 12, padding: "16px 18px", display: "flex", gap: 14, alignItems: "flex-start" }}>
              <span style={{ fontSize: 24 }}>✅</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--dark)", marginBottom: 4 }}>
                  {confirmedLeaf.tpl?.meta?.name?.[lang] || confirmedLeaf.tpl?.meta?.name?.en || confirmedLeaf.node.label}
                </div>
                <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.6 }}>
                  {confirmedLeaf.tpl?.meta?.desc?.[lang] || confirmedLeaf.tpl?.meta?.desc}
                </div>
                {confirmedLeaf.tpl?.meta?.badge && (
                  <span style={{
                    display: "inline-block", marginTop: 8, padding: "3px 10px",
                    borderRadius: 99, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                    background: confirmedLeaf.tpl.meta.badge === "scraping" ? "#dbeafe" :
                                confirmedLeaf.tpl.meta.badge === "filtering" ? "#fce7f3" : "#dcfce7",
                    color: confirmedLeaf.tpl.meta.badge === "scraping" ? "#1e40af" :
                           confirmedLeaf.tpl.meta.badge === "filtering" ? "#9d174d" : "#166534",
                  }}>
                    {confirmedLeaf.tpl.meta.badge}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
        {(path.length > 0 || confirmedLeaf) && mode === "template" && (
          <button className="btn-cancel" onClick={handleBack}>← {t.taskSelectionPage.backButton}</button>
        )}
        <button className="btn-cancel" onClick={onCancel}>{t.taskSelectionPage.cancel}</button>
        {mode === "scratch" && (
          <button className="btn-add" onClick={onCreateFromScratch}>{t.taskSelectionPage.continue} →</button>
        )}
        {mode === "template" && confirmedLeaf && (
          <button className="btn-add" onClick={() => handleUseTemplate(confirmedLeaf.node.templateId)}>
            {t.taskSelectionPage.useTemplate} →
          </button>
        )}
      </div>
    </div>
  );
}